import { useRef, useState } from "react";
import axios from "axios";
import { useAuth } from "@/auth/AuthContext";
import { toast } from "sonner";
import { Upload, Loader2 } from "lucide-react";
import { fileToUrl } from "@/content/ContentContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function FileUpload({
  kind = "image",
  value,
  onChange,
  label = "Upload file",
  accept,
  testid,
}) {
  const { authHeaders } = useAuth();
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);

  const handleFile = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    fd.append("kind", kind);
    setBusy(true);
    try {
      const res = await axios.post(`${API}/admin/uploads`, fd, {
        headers: { ...authHeaders() },
      });
      onChange?.(res.data.url, res.data);
      toast.success("Uploaded.");
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Upload failed.");
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const isImage = kind === "image" || (value && /\.(jpg|jpeg|png|gif|webp|svg)$/i.test(value));

  return (
    <div className="flex items-start gap-4">
      {value && isImage && (
        <img
          src={fileToUrl(value)}
          alt="preview"
          className="w-24 h-24 object-cover border border-[var(--line)]"
        />
      )}
      {value && !isImage && (
        <a
          href={fileToUrl(value)}
          target="_blank"
          rel="noreferrer"
          className="text-xs text-[var(--terracotta)] underline break-all"
        >
          Current file
        </a>
      )}
      <div className="flex-1">
        <input
          ref={inputRef}
          type="file"
          accept={accept || (kind === "image" ? "image/*" : "application/pdf,image/*")}
          className="hidden"
          data-testid={testid || `upload-${kind}`}
          onChange={(e) => handleFile(e.target.files?.[0])}
        />
        <button
          type="button"
          disabled={busy}
          onClick={() => inputRef.current?.click()}
          className="btn-secondary text-xs"
          style={{ padding: "0.6rem 1rem" }}
          data-testid={`${testid || `upload-${kind}`}-btn`}
        >
          {busy ? (
            <>
              <Loader2 size={14} className="animate-spin" /> Uploading…
            </>
          ) : (
            <>
              <Upload size={14} /> {label}
            </>
          )}
        </button>
        {value && (
          <p className="mt-2 text-xs text-[var(--ink-soft)] break-all">{value}</p>
        )}
      </div>
    </div>
  );
}
